import { useEffect, useState } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useProfile } from '@/api/profile'
import { useVoiceChoices } from '@/api/voiceChoices'
import { useCompany, useUpdateCompany, type CompanyUpdateInput, type RecordingNoticeMode } from '@/api/company'

/**
 * Recording of calls between colleagues. No phone number carries these calls,
 * so they get their own rules, modeled on a number's recording card.
 */
export function InternalCallsRecordingSection() {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const companyId = profile?.company ?? undefined
  const { data: company } = useCompany(companyId)
  const update = useUpdateCompany(companyId)
  const { data: voiceChoices, isLoading: voicesLoading } = useVoiceChoices()
  const [retention, setRetention] = useState('1825')
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!company) return
    setRetention(String(company.internal_recordings_retention_days ?? 1825))
    setMessage(company.internal_recording_notice_message ?? '')
  }, [company])

  if (!company) return null

  const save = (changes: CompanyUpdateInput) =>
    update.mutate(changes, {
      onSuccess: () => toast.success(t('manager.team.internalCalls.saved')),
      onError: () => toast.error(t('manager.team.internalCalls.saveError')),
    })

  const saveRetention = () => {
    const days = Number(retention)
    if (!Number.isInteger(days) || days < 1 || days > 3650) {
      setRetention(String(company.internal_recordings_retention_days))
      toast.error(t('manager.team.internalCalls.retentionInvalid'))
      return
    }
    if (days !== company.internal_recordings_retention_days) save({ internal_recordings_retention_days: days })
  }

  const on = company.record_internal_calls
  const allowed = company.recording_allowed !== false

  return (
    <section className="mt-10">
      <h2 className="text-base font-semibold text-slate-900 dark:text-white">
        {t('manager.team.internalCalls.title')}
      </h2>
      <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              {t('manager.team.internalCalls.record')}
            </p>
            <p className="text-xs mt-0.5 text-slate-500 dark:text-slate-400">
              {allowed ? t('manager.team.internalCalls.recordDesc') : t('manager.team.internalCalls.planRequired')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {update.isPending && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            <Switch
              id="internal-record"
              checked={on}
              disabled={!allowed || update.isPending}
              onCheckedChange={(v) => save({ record_internal_calls: v })}
              aria-label={t('manager.team.internalCalls.record')}
            />
          </div>
        </div>
        {on && (
          <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs text-slate-500" htmlFor="internal-notice-mode">
                  {t('manager.numbers.detail.noticeMode')}
                </label>
                <Select
                  value={company.internal_recording_notice_mode}
                  onValueChange={(v) => save({ internal_recording_notice_mode: v as RecordingNoticeMode })}
                >
                  <SelectTrigger id="internal-notice-mode" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{t('manager.numbers.detail.noticeAuto')}</SelectItem>
                    <SelectItem value="manual">{t('manager.team.internalCalls.noticeManual')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-500" htmlFor="internal-retention">
                  {t('manager.numbers.detail.retentionDays')}
                </label>
                <Input
                  id="internal-retention"
                  type="number"
                  min={1}
                  max={3650}
                  className="h-9"
                  value={retention}
                  onChange={(e) => setRetention(e.target.value)}
                  onBlur={saveRetention}
                />
              </div>
            </div>
            {company.internal_recording_notice_mode === 'auto' && (
              <div className="space-y-1">
                <label className="text-xs text-slate-500" htmlFor="internal-notice-voice">
                  {t('manager.team.internalCalls.voice')}
                </label>
                {/* Starts on the main number's voice; the manager can pick another. */}
                <Select
                  value={company.internal_recording_notice_voice || company.internal_recording_notice_voice_default}
                  onValueChange={(v) => save({ internal_recording_notice_voice: v })}
                  disabled={voicesLoading}
                >
                  <SelectTrigger id="internal-notice-voice" className="h-9 sm:w-72">
                    <SelectValue placeholder={t('manager.numbers.detail.voicePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {(voiceChoices ?? []).map((vc) => (
                      <SelectItem key={vc.value} value={vc.value}>{vc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {company.internal_recording_notice_mode === 'auto' && (
              <div className="space-y-1">
                <label className="text-xs text-slate-500" htmlFor="internal-notice-message">
                  {t('manager.numbers.detail.noticeMessage')}
                </label>
                {/* Read as written to both colleagues; nothing is read while it is empty. */}
                <Textarea
                  id="internal-notice-message"
                  rows={2}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onBlur={() => {
                    if (message !== (company.internal_recording_notice_message ?? '')) {
                      save({ internal_recording_notice_message: message.trim() })
                    }
                  }}
                />
              </div>
            )}
            {company.internal_recording_notice_mode === 'manual' && (
              <div className="rounded-md border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-700/50 p-2.5 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-700 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-800 dark:text-yellow-200">{t('manager.team.internalCalls.manualWarning')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
